import { unwrapResult } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ExternalLinkIcon } from '@rsos/assets/icons';
import { trackApplicationLaunch } from '@rsos/components/capstone/AppLauncher/appLauncherTrackings';
import { handleAppData } from '@rsos/components/capstone/AppLauncher/utils';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import { createDeeplinkURL } from '@rsos/reduxDataStore/src/features/appLauncher/appLauncherSlice';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import { notifyDefault, notifyError } from '@rsos/utils/toastUtils';
import callQueueSelectorNext from '../../../../selectors/callQueueSelectorNext';
import ExternalLinkCard from './ExternalLinkCard';

/**
 * External link cards displayed in the Unite event pane's tools tab.
 * @param {Array.<Object>} externalLinkApplications - List of external link applications.
 */
const ExternalLinkCards = ({ externalLinkApplications = [] }) => {
  const dispatch = useDispatch();

  const isTF911Enabled = useIsTF911Enabled();

  const orgID = useSelector(state => state.sinatra.user.currentOrg.id);

  const filteredCallsNext = useSelector(state =>
    callQueueSelectorNext(state, isTF911Enabled),
  );

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const mostRecentLocation = useMemo(() => {
    const { mostRecentLocation } = getMostRecentLocations(
      filteredCallsNext[selectedCallerID],
    );

    return mostRecentLocation || null;
  }, [filteredCallsNext, selectedCallerID]);

  const handleDisabled = useCallback(
    application => {
      // If the application can launch globally or the 911 event has a location,
      // then the application should not be disabled.
      if (application.details.launch_global || Boolean(mostRecentLocation)) {
        return false;
      }

      return true;
    },
    [mostRecentLocation],
  );

  // Copied from AppLauncher.js
  const handleDeeplink = useCallback(
    (application = null) => {
      // Track application launch
      trackApplicationLaunch({
        app: application,
        source: 'unite event pane',
      });

      const {
        id: appID,
        details: { data_fields, deeplink_endpoint: appEndpoint },
        display_name: appName,
      } = application;

      // appEndpoint is required to create a deeplink request
      if (!appEndpoint) {
        notifyError(
          `A required attribute (deeplink_endpoint) is missing for ${appName}`,
        );
        return;
      }

      let requestData = {
        appEndpoint,
        appID,
        appName,
        orgID,
      };

      if (data_fields?.length) {
        requestData = {
          ...requestData,
          appData: handleAppData(
            data_fields,
            selectedCallerID,
            mostRecentLocation,
          ),
        };
      }

      const toastParams = {
        message: `Opening ${requestData.appName} in new tab...`,
        customIcon: ExternalLinkIcon,
      };

      dispatch(createDeeplinkURL(requestData))
        .then(unwrapResult)
        .then(notifyDefault(toastParams))
        .then(response => {
          if (response?.url)
            window.open(response.url, '_blank', 'noopener noreferrer');
        })
        .catch(error => {
          notifyError(`Failed to open ${appName}: ${error.message}`);
        });
    },
    [dispatch, orgID, selectedCallerID, mostRecentLocation],
  );

  return externalLinkApplications.map(application => (
    <ExternalLinkCard
      data={application}
      isDisabled={handleDisabled(application)}
      key={application.id}
      onClick={handleDeeplink}
    />
  ));
};

export default ExternalLinkCards;
